import React, { lazy } from 'react';
import { Navigate, useOutlet } from 'react-router';

import { useAppSelector } from '@/App/hooks';
import { TermsOfUseStatus } from '@/App/types';
import { withAppProviders } from '@/App/withAppProviders';
import { SuspenseWrapper as Suspense } from '@/components/common/SuspenseWrapper';
import { AppLayout } from '@/components/layout/AppLayout';
import { selectShowError } from '@/features/ui/selectShowError';
import { selectTermsOfUseStatus } from '@/features/ui/selectTermsOfUseStatus';
import { withUser } from '@/features/user/withUser';
import { useAuth } from '@/hooks/useAuth';

const ServiceErrorPage = lazy(() =>
  import('@/components/pages/ServiceErrorPage').then(({ ServiceErrorPage }) => ({ default: ServiceErrorPage })),
);

const AppContent = lazy(() =>
  import('@/components/layout/AppContent').then(({ AppContent }) => ({ default: AppContent })),
);

const TermsOfUseNotAcceptedPage = lazy(() =>
  import('@/components/termsOfUse/TermsOfUseNotAcceptedPage').then(({ TermsOfUseNotAcceptedPage }) => ({
    default: TermsOfUseNotAcceptedPage,
  })),
);

interface ProtectedLayoutProps {
  anonymousUserRoute: string;
}

const ProtectedLayoutComponent: React.FC<ProtectedLayoutProps> = ({ anonymousUserRoute }) => {
  const { userAuthenticated } = useAuth();
  const showError = useAppSelector(selectShowError);
  const termsOfUseStatus = useAppSelector(selectTermsOfUseStatus);
  const outlet = useOutlet();

  if (!userAuthenticated) {
    return <Navigate to={anonymousUserRoute} />;
  }

  const content = showError ? (
    <Suspense>
      <ServiceErrorPage />
    </Suspense>
  ) : termsOfUseStatus === TermsOfUseStatus.ACCEPTED ? (
    <Suspense>
      <AppContent content={outlet} />
    </Suspense>
  ) : (
    <Suspense>
      <TermsOfUseNotAcceptedPage />
    </Suspense>
  );

  return <AppLayout content={content} />;
};

export const ProtectedLayout = withAppProviders(withUser(ProtectedLayoutComponent));
